import { Step as ModelStep } from "@redotech/redo-model/return-flow";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import QuestionIcon from "@redotech/redo-web/icon-old/question.svg";
import { groupBy } from "@redotech/util/map";
import { produce } from "immer";
import * as React from "react";
import {
  StepDownstream,
  StepId,
  StepType,
  StepTypeDetailsProps,
} from "../step";
import { containsImg } from "./step-rich-text-util";

export interface ChatChoiceState {
  name: string;
  next: StepId | null;
  nextName: string | null;
}

export interface ChatMultipleChoiceState {
  choices: ChatChoiceState[];
  message: string;
  messageHtml?: string;
  attempted?: boolean;
  customTitle?: string;
}

export const CHAT_MULTIPLE_CHOICE = (
  Details: React.ComponentType<StepTypeDetailsProps<ChatMultipleChoiceState>>,
): StepType<ChatMultipleChoiceState, ModelStep.ChatMultipleChoice> => ({
  Details: (props: StepTypeDetailsProps<ChatMultipleChoiceState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: "Multiple choice",
  valid(state) {
    return (
      !!state.choices.length &&
      state.choices.every(
        (choice) => choice.name && (choice.next || choice.nextName),
      ) &&
      !containsImg(state.messageHtml)
    );
  },
  Icon: QuestionIcon,
  description(state) {
    return state.message;
  },
  downstream(state) {
    const choices = groupBy(
      state.choices.filter((choice) => choice.next !== undefined),
      (choice) => choice.next,
    );
    const result: StepDownstream[] = [];
    for (const [next, c] of choices.entries()) {
      result.push({
        id: next!,
        label: c.length === 1 ? c[0]?.name : `${c.length} choices`,
      });
    }
    return result;
  },
  maxDownstream(state) {
    return (
      groupBy(
        state.choices.filter((choice) => choice.next !== undefined),
        (choice) => choice.next,
      ).size +
      state.choices.filter((choice) => choice.next === undefined).length
    );
  },
  empty: { choices: [], message: "", messageHtml: undefined },
  layout() {
    return BlockLayout.FULL;
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      for (const choice of state.choices) {
        if (choice.next === stepId) {
          choice.next = null;
        }
      }
    });
  },
  toModel(state, id) {
    return {
      customTitle: state.customTitle,
      type: ModelStep.CHAT_MULTIPLE_CHOICE,
      message: state.message,
      messageHtml: state.messageHtml,
      options: state.choices.map((choice) => ({
        name: choice.name,
        next: choice.next ? id(choice.next) : null,
        nextName: choice.nextName,
      })),
    };
  },
  fromModel(model, id) {
    return {
      customTitle: model.customTitle,
      choices: model.options.map((option) => ({
        name: option.name,
        next: option.next ? id(option.next) : null,
        nextName: option.nextName,
      })),
      message: model.message,
      messageHtml: model.messageHtml,
    };
  },
});
